import React from "react";
import styled, { css } from "styled-components";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { ClusterJobs, SystemJobs } from "@graylog/server-api";
import { ProgressBar, LinkToNode, RelativeTime, Icon } from "components/common";
import { Button, Badge } from "components/bootstrap";
import UserNotification from "util/UserNotification";
import { SYSTEM_JOBS_QUERY_KEY } from "components/systemjobs/useSystemJobs";
import translateApiError from "util/translateApiError";
var JobStatus = /* @__PURE__ */ ((JobStatus2) => {
  JobStatus2["Cancelled"] = "cancelled";
  JobStatus2["Complete"] = "complete";
  JobStatus2["Error"] = "error";
  JobStatus2["Paused"] = "paused";
  JobStatus2["Runnable"] = "runnable";
  JobStatus2["Running"] = "running";
  return JobStatus2;
})(JobStatus || {});
const BADGE_COLOR_BY_STATUS = {
  cancelled: "warning",
  complete: "success",
  error: "danger",
  queued: "gray",
  running: "primary"
};
const StyledProgressBar = styled(ProgressBar)`
  margin-top: 2px;
  margin-bottom: 5px;
`;
const JobWrap = styled.div`
  margin-bottom: 5px;
`;
const AcknowledgeButton = styled(Button)(
  ({ theme }) => css`
    && {
      color: ${theme.colors.text.primary};

      &:hover {
        color: ${theme.colors.variant.default};
      }
    }
  `
);
const SystemJob = ({ job }) => {
  var _a;
  const { t } = useTranslation("system");
  const queryClient = useQueryClient();
  const jobIsOver = job.job_status === "complete" /* Complete */ || job.percent_complete === 100 || job.job_status === "cancelled" /* Cancelled */ || job.job_status === "error" /* Error */;
  const mappedJobStatus = job.job_status === "runnable" /* Runnable */ ? "queued" : job.job_status;
  const _onAcknowledge = () => (e) => {
    e.preventDefault();
    SystemJobs.acknowledgeJob(job.id).then(() => {
      queryClient.invalidateQueries({ queryKey: SYSTEM_JOBS_QUERY_KEY });
    }).catch((error) => {
      var _a2;
      UserNotification.error((_a2 = translateApiError(error)) != null ? _a2 : error.responseMessage, t("system_job.acknowledge_failed"));
    });
  };
  const _onCancel = () => (e) => {
    e.preventDefault();
    if (window.confirm(t("system_job.cancel_confirm", { info: job.info }))) {
      ClusterJobs.cancelJob(job.id).then(() => {
        queryClient.invalidateQueries({ queryKey: SYSTEM_JOBS_QUERY_KEY });
      }).catch((error) => {
        var _a2;
        UserNotification.error((_a2 = translateApiError(error)) != null ? _a2 : error.responseMessage, t("system_job.cancel_failed"));
      });
    }
  };
  return /* @__PURE__ */ React.createElement("div", null, /* @__PURE__ */ React.createElement(JobWrap, null, /* @__PURE__ */ React.createElement(Icon, { name: "settings" }), " ", /* @__PURE__ */ React.createElement("span", { "data-toggle": "tooltip", title: job.name }, job.info), " ", t("system_job.on_node"), " ", /* @__PURE__ */ React.createElement(LinkToNode, { nodeId: job.node_id }), " ", /* @__PURE__ */ React.createElement(RelativeTime, { dateTime: job.started_at }), " ", /* @__PURE__ */ React.createElement("span", { "data-toggle": "tooltip", title: t("system_job.runtime", { duration: job.execution_duration }) }, /* @__PURE__ */ React.createElement(Badge, { color: (_a = BADGE_COLOR_BY_STATUS[mappedJobStatus]) != null ? _a : "gray", variant: "light", dot: true, style: { marginLeft: 4 } }, mappedJobStatus)), !jobIsOver && job.is_cancelable ? /* @__PURE__ */ React.createElement(Button, { type: "button", bsSize: "xs", bsStyle: "primary", className: "pull-right", onClick: _onCancel() }, t("system_job.cancel")) : /* @__PURE__ */ React.createElement(
    AcknowledgeButton,
    {
      type: "button",
      bsStyle: "link",
      onClick: _onAcknowledge(),
      bsSize: "xs",
      className: "pull-right",
      title: t("system_job.acknowledge")
    },
    /* @__PURE__ */ React.createElement(Icon, { name: "close" })
  )), !jobIsOver && job.provides_progress && /* @__PURE__ */ React.createElement(StyledProgressBar, { bars: [{ value: job.percent_complete, bsStyle: "info", animated: true }] }));
};
export default SystemJob;
